import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import cn from 'classnames';
import Headroom from 'react-headroom';
import { useSelector } from 'react-redux';

import {
  headerVisibleSel,
  isMobileSel,
  sidebarExpandedSel,
} from '@shared/duck/selectors/general.selectors';

import {
  headerHeight,
  headerHeightMobile,
  sidebarExpandedWidth,
  sidebarWidth,
  sidebarWidthMobile,
} from '../../constants';
import styles from './HeadroomAffix.module.scss';
import IsElementInViewport from './IsElementInViewport';

interface HeadroomAffixProps {
  children: React.ReactNode;
  disable?: boolean;
  disablePin?: boolean;
  upTolerance?: number;
  downTolerance?: number;
  pinStart?: number;
  offsetTop?: number;
  clearHeader?: boolean;
  clearSidebar?: boolean;
  wrapperStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  parent?: () => HTMLElement;
  className?: string;
  onPin?: () => void;
  onUnpin?: () => void;
  onUnfix?: () => void;
}

function HeadroomAffix({
  disable,
  disablePin,
  style,
  className,
  children,
  parent,
  pinStart = 0,
  upTolerance,
  downTolerance,
  wrapperStyle,
  offsetTop = 0,
  clearHeader,
  clearSidebar,
  onPin,
  onUnpin,
  onUnfix,
}: HeadroomAffixProps) {
  const [fixed, setFixed] = useState(false);
  const [, setPinned] = useState(false);
  const [isElementInViewport, setIsElementInViewport] = useState(true);
  const headroomInstanceRef = useRef<Headroom | null>(null);

  const headerVisible = useSelector(headerVisibleSel);
  const sidebarExpanded = useSelector(sidebarExpandedSel);
  const isMobile = useSelector(isMobileSel);

  const handlePin = useCallback(() => {
    setPinned(true);
    setFixed(true);
    onPin && onPin();
  }, [onPin]);

  const handleUnpin = useCallback(() => {
    setPinned(false);
    setFixed(true);
    onUnpin && onUnpin();
  }, [onUnpin]);

  const handleUnfix = useCallback(() => {
    setPinned(false);
    setFixed(false);
    onUnfix && onUnfix();
  }, [onUnfix]);

  const observerFunction = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (headroomInstanceRef?.current as any)?.setHeightOffset?.();
  }, [headroomInstanceRef]);

  const computedOffsetTop = useMemo(() => {
    let offset = offsetTop;
    if (clearHeader) {
      const height = isMobile ? headerHeightMobile : headerHeight;
      offset += headerVisible ? height : 0;
    }
    return offset;
  }, [clearHeader, headerVisible, isMobile, offsetTop]);

  const offsetLeft = useMemo(() => {
    let offset = 0;
    if (clearSidebar) {
      const width = isMobile
        ? sidebarWidthMobile
        : sidebarExpanded
        ? sidebarExpandedWidth
        : sidebarWidth;
      offset += width;
    }
    return offset;
  }, [clearSidebar, isMobile, sidebarExpanded]);

  const classNames = cn(className, styles.wrapper, {
    [styles.pin]: !disablePin,
    [styles.unfixed]: isElementInViewport,
  });

  const inlineStyle: React.CSSProperties = {
    left: `${offsetLeft}px`,
    ...style,
  };

  const transformWrapperStyle: React.CSSProperties = {
    transform: `translateY(${fixed ? computedOffsetTop : 0}px)`,
    transition: fixed ? 'transform 0.2s ease-in-out' : '',
  };

  useEffect(() => {
    const el = document.getElementById('BannerContainer');
    if (!el) return;

    const observer = new ResizeObserver(observerFunction);
    observer.observe(el);

    return () => observer.disconnect();
  }, [observerFunction]);

  return (
    <Headroom
      className={classNames}
      disable={disable || isElementInViewport}
      disableInlineStyles
      downTolerance={downTolerance}
      onPin={handlePin}
      onUnfix={handleUnfix}
      onUnpin={handleUnpin}
      parent={parent}
      pinStart={pinStart}
      ref={headroomInstanceRef}
      style={inlineStyle}
      upTolerance={upTolerance}
      wrapperStyle={wrapperStyle}
    >
      <IsElementInViewport
        offsetTop={computedOffsetTop + pinStart}
        onChange={setIsElementInViewport}
      >
        <div className={styles.transformWrapper} style={transformWrapperStyle}>
          {children}
        </div>
      </IsElementInViewport>
    </Headroom>
  );
}

export default HeadroomAffix;
